import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form-v7';

import { Separator } from '@parrot-software/design-system';
import { StepLayout, WizardFormField } from '@onboarding/presentation/components/shared';
import { AddressSearchInput } from '@onboarding/presentation/components/AddressSearchInput';
import { useStoreAdminInfo, useAddressSearch, useStoreBrands } from '@onboarding/presentation/hooks';
import type { WizardFormValues } from '@onboarding/presentation/types';
import { BrandFiscalSection } from './BrandFiscalSection';
import { ReceiptStepSkeleton } from './ReceiptStepSkeleton';

/**
 * Step 6 of the onboarding wizard: Receipt ticket configuration.
 *
 * Fetches the store admin info snapshot on mount and pre-fills the receipt
 * form fields. Also fetches store brands and initializes the brand fiscal
 * choices array. User edits are preserved on re-entry (only empty fields are
 * populated). If the fetch fails, a non-blocking error message is shown and
 * the form stays fully editable.
 */
export const ReceiptStep = () => {
  const { t } = useTranslation('onboarding');
  const { control, setValue, getValues } = useFormContext<WizardFormValues>();
  const { data: adminInfo, isError, isLoading: isLoadingAdmin } = useStoreAdminInfo();
  const { data: brands, isLoading: isLoadingBrands } = useStoreBrands();
  const isLoading = isLoadingAdmin || isLoadingBrands;
  const addressSearch = useAddressSearch();

  const legalName = useWatch({ control, name: 'receipt.legalName' });
  const rfc = useWatch({ control, name: 'receipt.rfc' });

  useEffect(() => {
    if (!adminInfo) return;

    const current = getValues('receipt');
    if (!current.businessName) setValue('receipt.businessName', adminInfo.storeName, { shouldValidate: false });
    if (!current.legalName) setValue('receipt.legalName', adminInfo.legalName, { shouldValidate: false });
    if (!current.rfc) setValue('receipt.rfc', adminInfo.rfc, { shouldValidate: false });
    if (!current.fiscalAddress) setValue('receipt.fiscalAddress', adminInfo.fiscalAddress, { shouldValidate: false });
  }, [adminInfo, setValue, getValues]);

  useEffect(() => {
    if (!brands?.length) return;
    if (!adminInfo) return;

    const current = getValues('receipt.brands');
    if (current.length > 0) return;

    const isSingleBrand = brands.length === 1;
    const hasFiscalData = !!(adminInfo?.legalName?.trim() && adminInfo?.rfc?.trim());
    const shouldUseCustom = isSingleBrand || !hasFiscalData;
    const defaultBrands = brands.map((brand) => ({
      brandUuid: brand.uuid,
      brandName: brand.name,
      useRegisteredData: shouldUseCustom ? 'false' : 'true',
      legalName: isSingleBrand ? adminInfo?.legalName ?? '' : '',
      rfc: isSingleBrand ? adminInfo?.rfc ?? '' : '',
      fiscalAddress: isSingleBrand ? adminInfo?.fiscalAddress ?? '' : '',
    }));
    setValue('receipt.brands', defaultBrands, { shouldValidate: false });
  }, [brands, adminInfo, setValue, getValues]);

  const brandFields = useWatch({ control, name: 'receipt.brands' }) ?? [];
  const hasSingleBrand = brandFields.length === 1;
  const hasMultipleBrands = brandFields.length > 1;
  const hasRegisteredFiscalData = !!(legalName?.trim() && rfc?.trim());

  return (
    <StepLayout description={t('steps.receipt.description')} title={t('steps.receipt.title')}>
      {isLoading && <ReceiptStepSkeleton />}

      {!isLoading && (
        <>
          {isError && <p className="text-xs text-destructive">{t('steps.receipt.errors.fetchAdminInfoFailed')}</p>}

          <div className="flex flex-col gap-4">
            <p className="text-base font-medium leading-6 text-foreground">{t('steps.receipt.sections.commercial')}</p>
            <div className="grid grid-cols-1 items-start gap-4">
              <Controller
                control={control}
                name="receipt.businessName"
                render={({ field, fieldState }) => (
                  <WizardFormField
                    error={fieldState.error?.message}
                    id="receipt-business-name"
                    label={t('steps.receipt.fields.businessName.label')}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder={t('steps.receipt.fields.businessName.placeholder')}
                    required
                    tooltip={t('steps.receipt.fields.businessName.tooltip')}
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>

          {hasSingleBrand && (
            <div className="flex flex-col gap-4">
              <p className="text-base font-medium leading-6 text-foreground">{t('steps.receipt.sections.fiscal')}</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="order-2 md:order-0">
                  <Controller
                    control={control}
                    name="receipt.brands.0.legalName"
                    render={({ field, fieldState }) => (
                      <WizardFormField
                        error={fieldState.error?.message}
                        id="brand-0-legal-name"
                        label={t('steps.receipt.fields.legalName.label')}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        placeholder={t('steps.receipt.fields.legalName.placeholder')}
                        required
                        tooltip={t('steps.receipt.fields.legalName.tooltip')}
                        value={field.value}
                      />
                    )}
                  />
                </div>
                <div className="order-1 md:order-0">
                  <Controller
                    control={control}
                    name="receipt.brands.0.rfc"
                    render={({ field, fieldState }) => (
                      <WizardFormField
                        error={fieldState.error?.message}
                        id="brand-0-rfc"
                        label={t('steps.receipt.fields.rfc.label')}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        placeholder={t('steps.receipt.fields.rfc.placeholder')}
                        required
                        tooltip={t('steps.receipt.fields.rfc.tooltip')}
                        value={field.value}
                      />
                    )}
                  />
                </div>
                <div className="order-3 col-span-1 md:col-span-2 lg:col-span-3">
                  <Controller
                    control={control}
                    name="receipt.brands.0.fiscalAddress"
                    render={({ field, fieldState }) => (
                      <AddressSearchInput
                        error={fieldState.error?.message}
                        isLoading={addressSearch.isLoadingPredictions}
                        label={t('steps.receipt.fields.fiscalAddress.label')}
                        onChange={(value) => {
                          addressSearch.setQuery(value);
                          field.onChange(value);
                        }}
                        onSelect={(address) => {
                          field.onChange(address.formattedAddress);
                          addressSearch.setQuery(address.formattedAddress);
                        }}
                        placeholder={t('steps.receipt.fields.fiscalAddress.placeholder')}
                        predictions={addressSearch.predictions}
                        required
                        selectPlace={addressSearch.selectPlace}
                        value={field.value}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          )}

          {hasMultipleBrands && hasRegisteredFiscalData && (
            <>
              <div className="flex flex-col gap-4">
                <p className="text-base font-medium leading-6 text-foreground">{t('steps.receipt.sections.fiscal')}</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="order-2 md:order-0">
                    <Controller
                      control={control}
                      name="receipt.legalName"
                      render={({ field, fieldState }) => (
                        <WizardFormField
                          error={fieldState.error?.message}
                          id="receipt-legal-name"
                          label={t('steps.receipt.fields.legalName.label')}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          placeholder={t('steps.receipt.fields.legalName.placeholder')}
                          required
                          tooltip={t('steps.receipt.fields.legalName.tooltip')}
                          value={field.value}
                        />
                      )}
                    />
                  </div>
                  <div className="order-1 md:order-0">
                    <Controller
                      control={control}
                      name="receipt.rfc"
                      render={({ field, fieldState }) => (
                        <WizardFormField
                          error={fieldState.error?.message}
                          id="receipt-rfc"
                          label={t('steps.receipt.fields.rfc.label')}
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          placeholder={t('steps.receipt.fields.rfc.placeholder')}
                          required
                          tooltip={t('steps.receipt.fields.rfc.tooltip')}
                          value={field.value}
                        />
                      )}
                    />
                  </div>
                  <div className="order-3 col-span-1 md:col-span-2 lg:col-span-3">
                    <Controller
                      control={control}
                      name="receipt.fiscalAddress"
                      render={({ field, fieldState }) => (
                        <AddressSearchInput
                          error={fieldState.error?.message}
                          isLoading={addressSearch.isLoadingPredictions}
                          label={t('steps.receipt.fields.fiscalAddress.label')}
                          onChange={(value) => {
                            addressSearch.setQuery(value);
                            field.onChange(value);
                          }}
                          onSelect={(address) => {
                            field.onChange(address.formattedAddress);
                            addressSearch.setQuery(address.formattedAddress);
                          }}
                          placeholder={t('steps.receipt.fields.fiscalAddress.placeholder')}
                          predictions={addressSearch.predictions}
                          required
                          selectPlace={addressSearch.selectPlace}
                          value={field.value}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <p className="text-base font-medium leading-6 text-foreground">{t('steps.receipt.sections.brands')}</p>
                {brandFields.map((brand, index) => (
                  <BrandFiscalSection
                    brandName={brand.brandName}
                    index={index}
                    isLast={index === brandFields.length - 1}
                    key={brand.brandUuid}
                    registeredLegalName={legalName || ''}
                    registeredRfc={rfc || ''}
                  />
                ))}
              </div>
            </>
          )}

          {hasMultipleBrands && !hasRegisteredFiscalData && (
            <div className="flex flex-col gap-6">
              <p className="text-base font-medium leading-6 text-foreground">{t('steps.receipt.sections.brands')}</p>
              {brandFields.map((brand, index) => (
                <div className="flex flex-col gap-4" key={brand.brandUuid}>
                  <span className="text-sm font-semibold leading-5 text-foreground">
                    {t('steps.receipt.brands.label', { name: brand.brandName })}
                  </span>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="order-2 md:order-0">
                      <Controller
                        control={control}
                        name={`receipt.brands.${index}.legalName`}
                        render={({ field, fieldState }) => (
                          <WizardFormField
                            error={fieldState.error?.message}
                            id={`brand-${index}-legal-name`}
                            label={t('steps.receipt.fields.legalName.label')}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            placeholder={t('steps.receipt.fields.legalName.placeholder')}
                            required
                            value={field.value}
                          />
                        )}
                      />
                    </div>
                    <div className="order-1 md:order-0">
                      <Controller
                        control={control}
                        name={`receipt.brands.${index}.rfc`}
                        render={({ field, fieldState }) => (
                          <WizardFormField
                            error={fieldState.error?.message}
                            id={`brand-${index}-rfc`}
                            label={t('steps.receipt.fields.rfc.label')}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            placeholder={t('steps.receipt.fields.rfc.placeholder')}
                            required
                            value={field.value}
                          />
                        )}
                      />
                    </div>
                    <div className="order-3 col-span-1 md:col-span-2 lg:col-span-3">
                      <Controller
                        control={control}
                        name={`receipt.brands.${index}.fiscalAddress`}
                        render={({ field, fieldState }) => (
                          <AddressSearchInput
                            error={fieldState.error?.message}
                            isLoading={addressSearch.isLoadingPredictions}
                            label={t('steps.receipt.fields.fiscalAddress.label')}
                            onChange={(value) => {
                              addressSearch.setQuery(value);
                              field.onChange(value);
                            }}
                            onSelect={(address) => {
                              field.onChange(address.formattedAddress);
                              addressSearch.setQuery(address.formattedAddress);
                            }}
                            placeholder={t('steps.receipt.fields.fiscalAddress.placeholder')}
                            predictions={addressSearch.predictions}
                            required
                            selectPlace={addressSearch.selectPlace}
                            value={field.value}
                          />
                        )}
                      />
                    </div>
                  </div>
                  {index < brandFields.length - 1 && <Separator />}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </StepLayout>
  );
};
